import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import { showUndoToast } from '../components/UndoToast';
import { plannerApi } from '../services/planner_api';
import type {
  ApproveItem,
  EventCreate,
  EventUpdate,
  OwnTaskCreate,
  OwnTaskUpdate,
  TaskDetailsData,
  TaskPrivateUpdate,
} from '../types/planner';
import { plannerKeys, refreshPlannerLists } from './plannerQuery';
import {
  restorePlanner,
  snapshotPlanner,
  updatePlannerCards,
  type CardUpdate,
  type PlannerSnapshot,
} from './plannerCache';
import { detailsKeyOf, type TaskRef } from './taskRef';
import { usePlannerFormat } from './usePlannerFormat';

interface Rollback {
  snapshot: PlannerSnapshot;
}

const approveRef = (item: ApproveItem): TaskRef =>
  item.student_assignment_id
    ? { kind: 'assignment', id: item.student_assignment_id }
    : { kind: 'task', id: item.task_id ?? '' };

function useOptimistic() {
  const queryClient = useQueryClient();
  const apply = useCallback(
    async (changes: Array<[TaskRef, CardUpdate]>): Promise<Rollback> => {
      const snapshot = await snapshotPlanner(queryClient);
      changes.forEach(([ref, update]) => updatePlannerCards(queryClient, ref, update));
      return { snapshot };
    },
    [queryClient],
  );
  const rollback = useCallback(
    (context: Rollback | undefined) => restorePlanner(queryClient, context?.snapshot),
    [queryClient],
  );
  const settle = useCallback(
    (details?: TaskDetailsData, ref?: TaskRef) => {
      const key = ref ? detailsKeyOf(ref) : undefined;
      if (details && key) queryClient.setQueryData(key, details);
      return refreshPlannerLists(queryClient, key);
    },
    [queryClient],
  );
  return { apply, rollback, settle };
}

export function useApprove() {
  const { t } = useTranslation('planner');
  const { apply, rollback, settle } = useOptimistic();
  return useMutation<unknown, Error, ApproveItem[], Rollback>({
    mutationFn: (items) => plannerApi.approve({ items }),
    onMutate: (items) =>
      apply(
        items.map((item) => [
          approveRef(item),
          (card) => ({ ...card, state: 'planned', suggested_by: null, day: item.day }),
        ]),
      ),
    onError: (_error, _items, context) => {
      rollback(context);
      toast.error(t('toast.saveError'));
    },
    onSettled: () => settle(),
  });
}

export function useTickTask() {
  const { t } = useTranslation('planner');
  const { apply, rollback, settle } = useOptimistic();
  return useMutation<TaskDetailsData, Error, { taskId: string; done: boolean }, Rollback>({
    mutationFn: ({ taskId, done }) => (done ? plannerApi.markDone(taskId) : plannerApi.markNotDone(taskId)),
    onMutate: ({ taskId, done }) =>
      apply([
        [
          { kind: 'task', id: taskId },
          (card) => ({ ...card, state: done ? 'done' : 'planned', timer_running_since: done ? null : card.timer_running_since }),
        ],
      ]),
    onError: (_error, _vars, context) => {
      rollback(context);
      toast.error(t('toast.saveError'));
    },
    onSettled: (details, _error, { taskId }) => settle(details, { kind: 'task', id: taskId }),
  });
}

export interface SetDayVariables {
  ref: TaskRef;
  plannedOn: string | null;
  plannedAt?: string | null;
  announce?: boolean;
}

export function useSetDay() {
  const { t } = useTranslation('planner');
  const fmt = usePlannerFormat();
  const { apply, rollback, settle } = useOptimistic();
  const mutation = useMutation<TaskDetailsData, Error, SetDayVariables, Rollback>({
    mutationFn: ({ ref, plannedOn, plannedAt }) => {
      const data = { planned_on: plannedOn, planned_at: plannedOn ? plannedAt ?? null : null };
      return ref.kind === 'assignment' ? plannerApi.planAssignment(ref.id, data) : plannerApi.planTask(ref.id, data);
    },
    onMutate: ({ ref, plannedOn, plannedAt }) =>
      apply([
        [
          ref,
          (card) => ({
            ...card,
            day: plannedOn,
            planned_at: plannedOn ? plannedAt ?? null : null,
            state: plannedOn ? 'planned' : 'not_planned',
            suggested_by: null,
            move_to: null,
            planned_after_due: !!plannedOn && !!card.due_on && plannedOn > card.due_on,
          }),
        ],
      ]),
    onSuccess: (_details, { ref, plannedOn, announce }) => {
      if (!announce || !plannedOn) return;
      showUndoToast(t('toast.plannedFor', { day: fmt.day(plannedOn) }), t('toast.undo'), () =>
        mutation.mutate({ ref, plannedOn: null }),
      );
    },
    onError: (_error, _vars, context) => {
      rollback(context);
      toast.error(t('toast.saveError'));
    },
    onSettled: (details, _error, { ref }) => settle(details, ref),
  });
  return mutation;
}

export function useUpdatePrivate() {
  const { t } = useTranslation('planner');
  const { apply, rollback, settle } = useOptimistic();
  return useMutation<TaskDetailsData, Error, { ref: TaskRef; data: TaskPrivateUpdate }, Rollback>({
    mutationFn: ({ ref, data }) =>
      ref.kind === 'assignment'
        ? plannerApi.updateAssignmentPrivate(ref.id, data)
        : plannerApi.updateTaskPrivate(ref.id, data),
    onMutate: ({ ref, data }) =>
      apply([
        [
          ref,
          (card) => ({
            ...card,
            ...(data.planned_minutes != null ? { planned_minutes: data.planned_minutes } : {}),
            ...(data.notes !== undefined ? { notes: data.notes } : {}),
            ...(data.checklist ? { checklist: data.checklist } : {}),
          }),
        ],
      ]),
    onError: (_error, _vars, context) => {
      rollback(context);
      toast.error(t('toast.saveError'));
    },
    onSettled: (details, _error, { ref }) => settle(details, ref),
  });
}

export function useLinkGoal() {
  const { t } = useTranslation('planner');
  const { settle } = useOptimistic();
  return useMutation<TaskDetailsData, Error, { ref: TaskRef; goalId: string | null }>({
    mutationFn: ({ ref, goalId }) =>
      ref.kind === 'assignment'
        ? plannerApi.setAssignmentGoal(ref.id, { goal_id: goalId })
        : plannerApi.updateTask(ref.id, { goal_id: goalId }),
    onError: () => toast.error(t('toast.saveError')),
    onSettled: (details, _error, { ref }) => settle(details, ref),
  });
}

export function useUpdateOwnTask() {
  const { t } = useTranslation('planner');
  const { apply, rollback, settle } = useOptimistic();
  return useMutation<TaskDetailsData, Error, { taskId: string; data: OwnTaskUpdate }, Rollback>({
    mutationFn: ({ taskId, data }) => plannerApi.updateTask(taskId, data),
    onMutate: ({ taskId, data }) =>
      apply([
        [
          { kind: 'task', id: taskId },
          (card) => ({
            ...card,
            ...(data.title ? { title: data.title } : {}),
            ...(data.due_on !== undefined ? { due_on: data.due_on } : {}),
          }),
        ],
      ]),
    onError: (_error, _vars, context) => {
      rollback(context);
      toast.error(t('toast.saveError'));
    },
    onSettled: (details, _error, { taskId }) => settle(details, { kind: 'task', id: taskId }),
  });
}

export function useCreateOwnTask() {
  const { t } = useTranslation('planner');
  const fmt = usePlannerFormat();
  const { settle } = useOptimistic();
  return useMutation({
    mutationFn: (data: OwnTaskCreate) => plannerApi.createTask(data),
    onSuccess: (card) => {
      if (card.state === 'not_planned' || !card.day) toast.success(t('toast.waits'));
      else if (card.state === 'suggested') toast.success(t('toast.suggestedFor', { day: fmt.day(card.day) }));
      else toast.success(t('toast.plannedFor', { day: fmt.day(card.day) }));
    },
    onError: () => toast.error(t('toast.saveError')),
    onSettled: () => settle(),
  });
}

export function useCancelTask() {
  const { t } = useTranslation('planner');
  const queryClient = useQueryClient();
  const { apply, rollback, settle } = useOptimistic();
  const remove = useMutation<void, Error, { taskId: string; context: Rollback }>({
    mutationFn: ({ taskId }) => plannerApi.deleteTask(taskId),
    onError: (_error, { context }) => {
      rollback(context);
      toast.error(t('toast.saveError'));
    },
    onSettled: () => settle(),
  });

  const cancel = useCallback(
    async (taskId: string) => {
      const context = await apply([[{ kind: 'task', id: taskId }, () => null]]);
      queryClient.removeQueries({ queryKey: plannerKeys.task(taskId), exact: true });
      showUndoToast(
        t('toast.taskCancelled'),
        t('toast.undo'),
        () => rollback(context),
        () => remove.mutate({ taskId, context }),
      );
    },
    [apply, queryClient, remove, rollback, t],
  );

  return { cancel, isPending: remove.isPending };
}

export function useCreateEvent() {
  const { t } = useTranslation('planner');
  const { settle } = useOptimistic();
  return useMutation({
    mutationFn: (data: EventCreate) => plannerApi.createEvent(data),
    onSuccess: () => toast.success(t('toast.eventAdded')),
    onError: () => toast.error(t('toast.saveError')),
    onSettled: () => settle(),
  });
}

export function useUpdateEvent() {
  const { t } = useTranslation('planner');
  const { settle } = useOptimistic();
  return useMutation({
    mutationFn: ({ eventId, data }: { eventId: string; data: EventUpdate }) => plannerApi.updateEvent(eventId, data),
    onSuccess: () => toast.success(t('toast.eventSaved')),
    onError: () => toast.error(t('toast.saveError')),
    onSettled: () => settle(),
  });
}

export function useDeleteEvent() {
  const { t } = useTranslation('planner');
  const { settle } = useOptimistic();
  return useMutation({
    mutationFn: ({ eventId, occurrenceDate }: { eventId: string; occurrenceDate?: string }) =>
      plannerApi.deleteEvent(eventId, occurrenceDate),
    onSuccess: () => toast.success(t('toast.eventDeleted')),
    onError: () => toast.error(t('toast.saveError')),
    onSettled: () => settle(),
  });
}
